import { logError } from '../lib/logger';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { InlineSelect } from './StatusBadge';
import { getEscalations, createEscalation, updateEscalation, deleteEscalation, getTeamMembers, getHotFiles, suggestEscalationAction } from '../lib/api';
import { StatusChip } from './enterprise/ui';
import { Sparkle } from '@phosphor-icons/react';
import { Plus, Trash, Siren, CheckCircle, Clock, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PRIORITIES = ['Critical', 'High', 'Normal', 'Low'];
const ESC_STATUSES = ['Open', 'In Progress', 'Resolved'];

const PRIORITY_COLORS = {
  'Critical': 'bg-red-600 text-white border-red-600',
  'High': 'bg-amber-500 text-white border-amber-500',
  'Normal': 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/30',
  'Low': 'bg-slate-400/15 text-slate-500 dark:text-slate-400 border-slate-400/25',
};

const STATUS_COLORS = {
  'Open': 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  'In Progress': 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  'Resolved': 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
};

// ─── Sub-components ───

function EscalationRow({ item, members: memberInitials, onInlineUpdate, onDelete, onSuggest, suggesting }) {
  return (
    <TableRow className="border-border hover:bg-muted/30 transition-colors" data-testid={`escalation-row-${item.id}`}>
      <TableCell className="font-medium text-foreground pl-4 text-sm">{item.borrower}</TableCell>
      <TableCell>
        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{item.from_member}</span>
      </TableCell>
      <TableCell>
        <InlineSelect value={item.to_member} options={memberInitials}
          onChange={(v) => onInlineUpdate(item.id, 'to_member', v)}
          testId={`inline-esc-to-${item.id}`} />
      </TableCell>
      <TableCell className="text-sm text-foreground max-w-[240px]">
        <p className="truncate">{item.reason}</p>
        {item.ai_next_action ? (
          <div className="mt-1 flex items-start gap-1.5" data-testid={`esc-ai-action-${item.id}`}>
            <Sparkle size={10} weight="fill" className="text-violet-400 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-violet-200 leading-snug"><span className="font-semibold text-violet-300">Next action: </span>{item.ai_next_action}</p>
          </div>
        ) : (
          <button onClick={() => onSuggest(item.id)} disabled={suggesting[item.id]}
            className="text-[10px] text-violet-400 hover:text-violet-300 font-semibold mt-1" data-testid={`esc-suggest-btn-${item.id}`}>
            <Sparkle size={9} weight="fill" className="inline mr-0.5" />{suggesting[item.id] ? 'Clara thinking...' : 'Suggest next action'}
          </button>
        )}
      </TableCell>
      <TableCell>
        <InlineSelect value={item.priority} options={PRIORITIES}
          onChange={(v) => onInlineUpdate(item.id, 'priority', v)}
          testId={`inline-esc-priority-${item.id}`} colorMap={PRIORITY_COLORS} />
      </TableCell>
      <TableCell>
        <InlineSelect value={item.status} options={ESC_STATUSES}
          onChange={(v) => onInlineUpdate(item.id, 'status', v)}
          testId={`inline-esc-status-${item.id}`} colorMap={STATUS_COLORS} />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground max-w-[150px]">
        <p className="truncate">{item.notes || '-'}</p>
      </TableCell>
      <TableCell className="pr-4">
        <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)}
          className="h-7 w-7 text-red-400 hover:text-red-600" data-testid={`delete-escalation-${item.id}`}>
          <Trash size={14} />
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ─── Main Component ───

export default function Escalations() {
  const [items, setItems] = useState([]);
  const [suggesting, setSuggesting] = useState({});
  const [members, setMembers] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ borrower: '', from_member: '', to_member: '', reason: '', priority: 'Normal', notes: '', file_id: '' });
  const [filterStatus, setFilterStatus] = useState('all');

  const loadData = useCallback(async () => {
    try {
      const [e, m, f] = await Promise.all([getEscalations(), getTeamMembers(), getHotFiles()]);
      setItems(e); setMembers(m); setFiles(f);
    } catch (e) { logError('Escalations load error:', e); toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); const iv = setInterval(loadData, 5000); return () => clearInterval(iv); }, [loadData]);

  const memberInitials = useMemo(() => members.map(m => m.initials), [members]);

  const handleInlineUpdate = async (id, field, value) => {
    try {
      await updateEscalation(id, { [field]: value });
      setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value, ...(value === 'Resolved' && field === 'status' ? { resolved_at: new Date().toISOString() } : {}) } : i));
      if (field === 'status' && value === 'Resolved') toast.success('Escalation resolved');
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleSave = async () => {
    if (!form.borrower.trim() || !form.from_member || !form.to_member || !form.reason.trim()) {
      toast.error('Fill all required fields'); return;
    }
    try {
      await createEscalation(form);
      toast.success('Escalation created');
      setDialogOpen(false);
      setForm({ borrower: '', from_member: '', to_member: '', reason: '', priority: 'Normal', notes: '', file_id: '' });
      loadData();
    } catch (e) { toast.error('Failed to create'); }
  };

  const handleDelete = async (id) => {
    try { await deleteEscalation(id); toast.success('Deleted'); loadData(); } catch (e) { toast.error('Failed'); }
  };

  const handleSuggest = async (id) => {
    setSuggesting(p => ({ ...p, [id]: true }));
    try {
      const r = await suggestEscalationAction(id);
      if (r.suggestion) { setItems(prev => prev.map(x => x.id === id ? { ...x, ai_next_action: r.suggestion } : x)); toast.success('Clara suggested a next action'); }
      else { toast.info('Clara could not suggest anything specific'); }
    } catch (e) { toast.error('Clara failed'); }
    finally { setSuggesting(p => ({ ...p, [id]: false })); }
  };

  const sorted = useMemo(() => {
    const filtered = filterStatus === 'all' ? items : items.filter(i => i.status === filterStatus);
    return [...filtered].sort((a, b) => {
      const p = { Critical: 0, High: 1, Normal: 2, Low: 3 };
      const s = { Open: 0, 'In Progress': 1, Resolved: 2 };
      return (s[a.status] || 0) - (s[b.status] || 0) || (p[a.priority] || 2) - (p[b.priority] || 2);
    });
  }, [items, filterStatus]);

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="escalations-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="escalations-view">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex gap-1 bg-muted rounded-md p-0.5">
          {['all', 'Open', 'In Progress', 'Resolved'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              data-testid={`esc-filter-${s.toLowerCase().replace(' ', '-')}`}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                filterStatus === s ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-primary text-primary-foreground hover:opacity-90 h-9" data-testid="add-escalation-btn">
          <Plus size={16} className="mr-1.5" /> Escalate
        </Button>
      </div>

      <Card className="border border-border bg-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pl-4">Borrower</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">From</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">To</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Reason</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Priority</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Status</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Notes</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pr-4 w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No escalations</TableCell></TableRow>
              ) : sorted.map((item) => (
                <EscalationRow key={item.id} item={item} members={memberInitials}
                  onInlineUpdate={handleInlineUpdate} onDelete={handleDelete}
                  onSuggest={handleSuggest} suggesting={suggesting} />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Team Members Reference */}
      <Card className="border border-border bg-card" data-testid="team-members-card">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Team Members</h3>
          <div className="flex flex-wrap gap-2">
            {members.map(m => (
              <div key={m.id} className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted border border-border" data-testid={`team-member-${m.initials.toLowerCase()}`}>
                <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-primary text-primary-foreground text-[10px] font-bold">{m.initials}</span>
                <div>
                  <p className="text-xs font-medium text-foreground">{m.name}</p>
                  {m.role && <p className="text-[10px] text-muted-foreground">{m.role}</p>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="escalation-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><Siren size={20} className="text-red-500" /> Create Escalation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Borrower / File</label>
                <Select value={form.borrower || 'custom'} onValueChange={(v) => {
                  if (v === 'custom') return;
                  const f = files.find(fl => fl.borrower === v);
                  setForm(p => ({ ...p, borrower: v, file_id: f?.id || '' }));
                }}>
                  <SelectTrigger className="border-border h-9" data-testid="select-esc-borrower"><SelectValue placeholder="Select file" /></SelectTrigger>
                  <SelectContent>
                    {files.filter(f => f.status !== 'Done').map(f => (
                      <SelectItem key={f.id} value={f.borrower}>{f.borrower} ({f.sa_initials})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input value={form.borrower} onChange={(e) => setForm(p => ({ ...p, borrower: e.target.value }))}
                  placeholder="Or type name..." className="border-border h-9 mt-1.5" data-testid="input-esc-borrower" />
              </div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger className="border-border h-9" data-testid="select-esc-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">From (Who is escalating)</label>
                <Select value={form.from_member} onValueChange={(v) => setForm(p => ({ ...p, from_member: v }))}>
                  <SelectTrigger className="border-border h-9" data-testid="select-esc-from"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} - {m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">To (Escalate to)</label>
                <Select value={form.to_member} onValueChange={(v) => setForm(p => ({ ...p, to_member: v }))}>
                  <SelectTrigger className="border-border h-9" data-testid="select-esc-to"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} - {m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Reason</label>
              <Input value={form.reason} onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))}
                placeholder="Why escalating?" className="border-border h-9" data-testid="input-esc-reason" /></div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Additional Notes</label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                className="border-border min-h-[60px]" data-testid="input-esc-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border" data-testid="cancel-escalation-btn">Cancel</Button>
            <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-escalation-btn">
              <Siren size={14} className="mr-1.5" /> Escalate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
